import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BookOpen, ArrowLeft, Calendar, MessageSquare, PlayCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function MyEnrolledCourses({ user }) {
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    try {
      const response = await fetch(`${API}/enrollments/my`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setEnrollments(data);
        
        // Load course details for each enrollment
        const coursePromises = data.map(async (enrollment) => {
          const courseRes = await fetch(`${API}/courses/${enrollment.course_id}`, {
            headers: getAuthHeaders()
          });
          if (courseRes.ok) {
            return await courseRes.json();
          }
          return null;
        });
        
        const coursesData = await Promise.all(coursePromises);
        const coursesMap = {};
        coursesData.forEach((course) => {
          if (course) {
            coursesMap[course.course_id] = course;
          }
        });
        setCourses(coursesMap);
      }
    } catch (error) {
      toast.error('Failed to load enrolled courses');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAEDCD] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-[#3A5A40] mx-auto mb-4 animate-pulse" />
          <p className="text-[#588157]">Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center gap-2 sm:gap-3">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-lg sm:text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              My Enrolled Courses
            </h1>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 md:p-12">
        <div className="mb-6 sm:mb-8">
          <p className="text-base sm:text-lg text-[#588157]">
            Access your enrolled courses, daily activities, and discussions
          </p>
        </div>

        {enrollments.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <BookOpen className="w-16 h-16 text-[#A3B18A] mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-bold text-[#344E41] mb-2">No Enrolled Courses</h3>
            <p className="text-[#588157] mb-4">You haven't enrolled in any courses yet.</p>
            <Button
              onClick={() => navigate('/courses')}
              className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
            >
              Browse Courses
            </Button>
          </Card>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {enrollments.map((enrollment) => {
              const course = courses[enrollment.course_id];
              if (!course) return null;
              
              return (
                <Card 
                  key={enrollment.enrollment_id}
                  className="bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl overflow-hidden"
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Course Thumbnail */}
                      {course.thumbnail && (
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-full sm:w-32 h-32 object-cover rounded-xl"
                        />
                      )}
                      
                      {/* Course Info */}
                      <div className="flex-1">
                        <h3 className="text-xl sm:text-2xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                          {course.title}
                        </h3>
                        <p className="text-sm sm:text-base text-[#588157] mb-3 line-clamp-2">
                          {course.description}
                        </p>
                        
                        {/* Progress */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm text-[#588157] mb-1">
                            <span>Progress</span>
                            <span>{enrollment.progress || 0}%</span>
                          </div>
                          <div className="h-2 bg-[#E9EDC9] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#3A5A40] rounded-full transition-all duration-300"
                              style={{ width: `${enrollment.progress || 0}%` }}
                            />
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                          <Button
                            onClick={() => navigate(`/courses/${course.course_id}/view`)}
                            data-testid={`continue-course-${course.course_id}`}
                            className="bg-[#3A5A40] text-white hover:bg-[#344E41] text-sm"
                          >
                            <PlayCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Continue
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/my-courses/${course.course_id}/activities`)}
                            data-testid={`activities-${course.course_id}`}
                            className="border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white text-sm"
                          >
                            <Calendar className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Daily Activities
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/my-courses/${course.course_id}/discussions`)}
                            data-testid={`discussions-${course.course_id}`}
                            className="border-[#588157] text-[#588157] hover:bg-[#588157] hover:text-white text-sm"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Discussions
                          </Button>
                        </div>
                      </div>
                      
                      {/* Arrow for mobile */}
                      <ChevronRight className="hidden sm:block w-6 h-6 text-[#A3B18A] self-center" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
